import { useState } from "react";
import { useLocation } from "wouter";
import type { Project, Service } from "../../api/types";
import { useServices } from "../../api/queries";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { ServiceStatusPill } from "../../components/StatePill";
import { CreateServiceModal } from "../services/CreateServiceModal";
import { PlacementsSummaryCell } from "../services/PlacementsSummaryCell";
import { ImportComposeModal } from "./ImportComposeModal";

const COLUMNS: DataTableColumn<Service>[] = [
  { key: "name", header: "Name", cell: (service) => service.name, className: "data-table__primary" },
  { key: "status", header: "Status", cell: (service) => <ServiceStatusPill status={service.status} /> },
  {
    key: "source",
    header: "Image",
    cell: (service) => service.image || "—",
    className: "data-table__mono",
  },
  { key: "desiredCount", header: "Desired count", cell: (service) => service.desiredCount, className: "data-table__mono" },
  {
    key: "placements",
    header: "Where it runs",
    cell: (service) => <PlacementsSummaryCell projectId={service.projectId} serviceId={service.id} />,
  },
  {
    key: "updatedAt",
    header: "Updated",
    cell: (service) => new Date(service.updatedAt).toLocaleString(),
    className: "data-table__secondary",
  },
];

/**
 * The Services tab (IA slice 3, docs/product/ui-information-architecture.md §3): a
 * project's full, manageable service list — create/import, the paginated table, and a
 * service's own detail on row click. Moved here verbatim from the old top-level
 * `ProjectDetailPage` body (pre-slice-3), which mounted this same content directly at
 * `/projects/:id` before Overview/Corpus/Sessions/Services became distinct tabs.
 */
export function ProjectServicesTab({ project, canManage }: { project: Project; canManage: boolean }) {
  const [, navigate] = useLocation();
  const [createServiceOpen, setCreateServiceOpen] = useState(false);
  const [importComposeOpen, setImportComposeOpen] = useState(false);

  const servicesPaginator = usePaginator(project.id);
  const {
    data: servicesPage,
    isPending: servicesPending,
    isError: servicesIsError,
    error: servicesError,
    refetch: refetchServices,
    isFetching: servicesFetching,
  } = useServices(project.id, { cursor: servicesPaginator.cursor });

  const newServiceButton = (
    <Button variant="primary" onClick={() => setCreateServiceOpen(true)}>
      New service
    </Button>
  );

  const importComposeButton = (
    <Button variant="secondary" onClick={() => setImportComposeOpen(true)}>
      Import from compose
    </Button>
  );

  return (
    <div className="project-services-tab">
      <div className="project-detail__services-header">
        <h2>Services</h2>
        {canManage && (
          <div className="project-detail__services-actions">
            {importComposeButton}
            {newServiceButton}
          </div>
        )}
      </div>

      <QueryStates
        isLoading={servicesPending}
        loadingLabel="Loading services…"
        isError={servicesIsError}
        error={servicesError}
        onRetry={refetchServices}
        errorDescription={
          servicesError instanceof Error
            ? servicesError.message
            : "Couldn't load services. Check your connection and try again."
        }
        isEmpty={Boolean(servicesPage && servicesPage.items.length === 0)}
        empty={
          <EmptyState
            title="No services in this project yet"
            description="Import a compose file to get started, or create a service by hand to define an image and a desired instance count."
            action={
              canManage ? (
                <div className="project-detail__services-actions">
                  {importComposeButton}
                  {newServiceButton}
                </div>
              ) : undefined
            }
          />
        }
      >
        {servicesPage && (
          <>
            <DataTable
              columns={COLUMNS}
              rows={servicesPage.items}
              rowKey={(service) => service.id}
              onRowClick={(service) => navigate(`/projects/${project.id}/services/${service.id}`)}
              ariaBusy={servicesFetching}
            />
            <PaginatorControls
              itemCount={servicesPage.items.length}
              nextCursor={servicesPage.nextCursor}
              paginator={servicesPaginator}
              noun="services"
            />
          </>
        )}
      </QueryStates>

      {canManage && (
        <CreateServiceModal
          open={createServiceOpen}
          projectId={project.id}
          onClose={() => setCreateServiceOpen(false)}
        />
      )}

      {canManage && (
        <ImportComposeModal
          open={importComposeOpen}
          projectId={project.id}
          onClose={() => setImportComposeOpen(false)}
        />
      )}
    </div>
  );
}
